import React from 'react';
import { StudentProgress, StudentAchievement, type RacquetSportId } from '@court-sync/shared';
import { Card, CardHeader, CardContent } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { 
  TrendingUp, 
  Award, 
  Star,
  Calendar,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { formatAssessmentSkillLabel, getAssessmentSkillsForSport } from '@/lib/sportPresentation';

interface ProgressChartProps {
  progress: StudentProgress;
  showAchievements?: boolean;
  className?: string;
  /** When set, skill bars use sport-specific keys instead of tennis defaults. */
  sportId?: RacquetSportId;
}

interface SkillBarProps {
  skill: string;
  label: string;
  rating: number;
  maxRating?: number;
}

const SkillBar: React.FC<SkillBarProps> = ({ label, rating, maxRating = 5 }) => {
  const percentage = (rating / maxRating) * 100;

  const getBarColor = (rating: number): string => {
    if (rating >= 4) return 'bg-green-500';
    if (rating >= 3) return 'bg-blue-500';
    if (rating >= 2) return 'bg-yellow-500';
    return 'bg-gray-400';
  };

  return (
    <div className="flex items-center gap-3">
      <div className="w-24 text-sm text-gray-600 dark:text-gray-400">
        {label}
      </div>
      <div className="flex-1">
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div
            className={cn('h-2 rounded-full transition-all duration-500', getBarColor(rating))}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
      <div className="w-8 text-right text-sm font-medium text-gray-700 dark:text-gray-300">
        {rating}/{maxRating}
      </div>
    </div>
  );
};

const AchievementBadge: React.FC<{ achievement: StudentAchievement }> = ({ achievement }) => {
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'tournament':
        return '🏆';
      case 'skill':
        return '⭐';
      case 'participation':
        return '🎯';
      case 'improvement':
        return '📈';
      default:
        return '🎖️';
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="flex items-start gap-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
      <div className="text-2xl">{getCategoryIcon(achievement.category)}</div>
      <div className="flex-1">
        <div className="font-medium text-gray-900 dark:text-white">
          {achievement.title}
        </div>
        {achievement.description && (
          <div className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            {achievement.description}
          </div>
        )}
        <div className="text-xs text-gray-500 dark:text-gray-500 mt-1 flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {formatDate(achievement.earnedAt)}
        </div>
      </div>
    </div>
  );
};

const getSkillLevelVariant = (level: string): 'default' | 'secondary' | 'success' | 'warning' => {
  switch (level) {
    case 'advanced':
      return 'success';
    case 'intermediate':
      return 'warning';
    default:
      return 'secondary';
  }
};

export const ProgressChart: React.FC<ProgressChartProps> = ({
  progress,
  showAchievements = true,
  className,
  sportId = 'tennis',
}) => {
  const skillKeys = getAssessmentSkillsForSport(sportId);
  const skillEntries = Object.entries(progress.skillRatings).filter(
    ([, rating]) => rating != null
  ) as [string, number][];
  const avgRating = skillEntries.length > 0
    ? skillEntries.reduce((sum, [, rating]) => sum + rating, 0) / skillEntries.length
    : 0;

  const formatDate = (date: Date | undefined) => {
    if (!date) return 'Not assessed';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className={cn('space-y-6', className)}>
      {/* Skill Ratings Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Skill Assessment
            </h3>
            <Badge variant={getSkillLevelVariant(progress.skillLevel)}>
              {progress.skillLevel}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {/* Overall Stats */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Average Rating</div>
              <div className="flex items-center gap-2 mt-1">
                <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                <span className="text-2xl font-bold text-gray-900 dark:text-white">
                  {avgRating.toFixed(1)}
                </span>
                <span className="text-gray-500 dark:text-gray-400">/ 5</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500 dark:text-gray-400">Last Assessment</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                {formatDate(progress.lastAssessmentDate)}
              </div>
            </div>
          </div>

          {/* Skill Bars */}
          <div className="space-y-4">
            {(skillEntries.length > 0
              ? skillEntries.map(([key]) => key)
              : [...skillKeys]
            ).map((skill) => {
              const rating = (progress.skillRatings as Record<string, number | undefined>)[skill];
              if (rating === undefined) return null;
              return (
                <SkillBar
                  key={skill}
                  skill={skill}
                  label={formatAssessmentSkillLabel(skill)}
                  rating={rating}
                />
              );
            })}
          </div>

          {/* Coach Notes on Progress */}
          {progress.notes && (
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Coach Notes
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 italic">
                "{progress.notes}"
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Achievements Card */}
      {showAchievements && progress.achievements.length > 0 && (
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-500" />
              Achievements
              <Badge variant="secondary">{progress.achievements.length}</Badge>
            </h3>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2">
              {progress.achievements.map((achievement) => (
                <AchievementBadge key={achievement.id} achievement={achievement} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProgressChart;
